<!-- Copyright (c) 2023, Alix Mougenot (Au pied des Arbres), alix.mougenot@gmail.com -->
<!-- See license file. -->
<script setup lang="ts">
import type { Actor, Dentist, HealthCenter } from "@/types";
import { pictogram } from "@/types";

import { ref, computed, inject } from "vue";
import FormInput from "../FormInput.vue";
import { findStakeholder, makeSearchEngine } from "../../logic/search";
import { actorsKey, dentistKey, healthCentersKey } from "../../logic/provide";

const emit = defineEmits<{
  selected: [Dentist | Actor | HealthCenter];
  closed: [];
}>();

const props = defineProps<{
  label: string | null;
  cta: string;
  includeActors: boolean;
  includeHealthCenters: boolean;
  fastSelection?: Array<string>;
}>();

// structure
const isOpen = ref(props.label === null); // When no label is passed start selection
const open = () => {
  isOpen.value = true;
};
const close = () => {
  searchInput.value = "";
  isOpen.value = false;
  emit("closed");
};

// Search
const searchInput = ref("");
const allActors = inject(actorsKey);
const allDentists = inject(dentistKey);
const allHealthCenters = inject(healthCentersKey);

const selected = (model: Actor | Dentist | HealthCenter) => {
  emit("selected", model);
  close();
};

const searchSource = computed(() => {
  if (!allActors || !allDentists || !allHealthCenters) {
    console.error("Missing at least one search source");
    return [];
  }
  const actors = props.includeActors
    ? allActors?.value.list.filter((a) => a.type !== "Centre de Santé") || []
    : [];
  const healthCenters = allHealthCenters?.value.list || [];
  const dentists = allDentists?.value.list || [];
  const students =
    allActors?.value.list.filter((a) => a.type === "Praticien Etudiant") || [];

  return [...actors, ...dentists, ...students, ...healthCenters];
});

const { searchResult } = makeSearchEngine(
  {
    includeScore: true,
    ignoreLocation: true,
    findAllMatches: false,
    minMatchCharLength: 3,
    keys: [
      { name: "id" },
      { name: "first_name", weight: 2 },
      { name: "last_name", weight: 3 },
      {
        name: "full_name",
        getFn: (dentist: Dentist | Actor | HealthCenter) =>
          "first_name" in dentist && "last_name" in dentist
            ? dentist.last_name + " " + dentist.first_name
            : "",
        weight: 6,
      },
      {
        name: "full_name_inverted",
        getFn: (dentist: Dentist | Actor | HealthCenter) =>
          "first_name" in dentist && "last_name" in dentist
            ? dentist.first_name + " " + dentist.last_name
            : "",
        weight: 5,
      },
      "contact.email",
      "notes",
      { name: "cdo_id", weight: 3 },
      { name: "name", weight: 4 },
      { name: "other_name", weight: 4 },
      { name: "institution_name", weight: 4 },
      { name: "contact_imported.address", weight: 2 },
      { name: "contact_imported.zipcode", weight: 3 },
    ],
  },
  searchInput,
  searchSource
);

const foundFastAccess = computed(() => {
  if (!props.fastSelection) {
    return { found: [], notFound: [] };
  } else if (!allActors || !allDentists || !allHealthCenters) {
    console.error("Missing at least one search source");
    return { found: [], notFound: [] };
  } else {
    const refs = props.fastSelection.map((id) => {
      return {
        id: id,
        name: "",
        type: null,
      };
    });
    return findStakeholder(refs, allActors, allDentists, allHealthCenters);
  }
});

const foundEntities = computed(() => {
  const prefix = foundFastAccess.value?.found || [];
  return searchResult.value?.length
    ? prefix.concat(
        searchResult.value.slice(0, 8) as Array<Actor | Dentist | HealthCenter>
      )
    : prefix;
});
</script>

<template>
  <div class="search">
    <div class="input-container">
      <FormInput
        v-if="isOpen"
        v-model="searchInput"
        :label="props.label || ''"
        type="text"
      />
    </div>
    <div v-if="isOpen">
      <span class="button right" @click.stop="close"> Annuler </span>
    </div>

    <span v-if="!isOpen" class="button align-right" @click.stop="open">{{
      props.label
    }}</span>
  </div>
  <div class="listing">
    <div
      class="entry"
      v-for="model of foundEntities"
      :key="model.id"
      @click.stop="selected(model)"
    >
      <!-- health center -->
      <div
        class="content"
        v-if="pictogram(model) == 'institute' && 'finess' in model"
      >
        <img src="@/assets/icons/medical-center-bold.svg" class="icon" />
        <h3>
          {{ model.name || "sans nom" }}
        </h3>
        <template v-if="model.contact_imported.zipcode">
          <span> - </span>
          <span> {{ model.contact_imported.zipcode }} </span>
        </template>
      </div>

      <!-- institution -->
      <div
        class="content"
        v-if="pictogram(model) == 'institute' && 'institution_name' in model"
        :key="model.id"
      >
        <img src="@/assets/icons/building-bold.svg" class="icon" />
        <h3>
          {{ model.institution_name }}
        </h3>
        <span> ({{ "type" in model && model.type }})</span>
        <template v-if="model.contact.zipcode">
          <span> - </span>
          <span> {{ model.contact.zipcode }} </span>
        </template>
      </div>

      <!-- person -->
      <div
        class="content"
        v-if="pictogram(model) == 'human' && !('finess' in model)"
      >
        <img src="@/assets/icons/actor-bold.svg" class="icon" />
        <span>{{ model.title }} </span>
        <h3>{{ model.last_name }}</h3>
        <span>{{ model.first_name }} </span>
        <template v-if="model.contact.zipcode">
          <span> - </span>
          <span> {{ model.contact.zipcode }} </span>
        </template>
      </div>

      <div
        class="content"
        v-if="pictogram(model) == 'student' && !('finess' in model)"
      >
        <img src="@/assets/icons/student-bold.svg" class="icon" />
        <span>{{ model.title }} </span>
        <h3>{{ model.last_name }}</h3>
        <span>{{ model.first_name }} </span>
        <template v-if="model.contact.zipcode">
          <span> - </span>
          <span> {{ model.contact.zipcode }} </span>
        </template>
      </div>

      <!-- dentists -->
      <div
        class="content"
        v-if="pictogram(model) == 'practitian' && !('finess' in model)"
      >
        <img src="@/assets/icons/id-card-bold.svg" class="icon" />
        <span>{{ model.title }} </span>
        <h3>{{ model.last_name }}</h3>
        <span>{{ model.first_name }} </span>
        <span> - </span>
        <span> ({{ "dep_id" in model && model.dep_id }}) </span>
        <span> {{ "cdo_id" in model && model.cdo_id }} </span>
        <template v-if="model.contact.zipcode">
          <span> - </span>
          <span> {{ model.contact.zipcode }} </span>
        </template>
      </div>

      <div class="action">
        <span>{{ props.cta }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
h2,
h3,
span {
  color: var(--vt-c-purple);
}

.button {
  &.right {
    padding-left: 1em;
    padding-right: 1em;
  }
}

.listing {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 0.5em;

  margin-left: 1.5em;

  .entry {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: baseline;
    justify-content: space-between;

    .content {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      column-gap: 0.4em;

      align-items: baseline;
    }

    .action {
      margin-right: 0.4em;
      display: none;
    }

    &:hover {
      background-color: var(--color-background-soft);
      cursor: pointer;

      .action {
        display: inline-block;
      }
    }
  }
}

.input-container {
  flex-grow: 1;
}

.search {
  display: flex;
  margin-bottom: 0.4em;
  align-items: center;
}

.align-right {
  text-align: right;
  margin-left: auto;
  margin-right: 1.3em;
}
</style>
